import {
  parseMarkdown,
  serializeMarkdown,
  toPlainText,
  type MarkdownNode,
} from '@alphadoc/alphadoc-text-editor/markdown';

export type CommentPayload = {
  comment: string;
  commentMarkdown: string;
};

const ACTION_BLOCK_BOUNDARY_RESERVED_LENGTH = 1;

export function plainToMarkdown(plain: string) {
  return serializeMarkdown({
    type: 'doc',
    content: plain.split('\n').map((line) => ({
      type: 'paragraph',
      content: line ? [{ type: 'text', text: line }] : [],
    })),
  });
}

export function toCommentPayload(markdown: string): CommentPayload {
  const comment = toPlainText(markdown);

  return {
    comment,
    commentMarkdown: plainToMarkdown(comment) === markdown ? '' : markdown,
  };
}

function paragraph(text: string): MarkdownNode {
  return {
    type: 'paragraph',
    content: [{ type: 'text', text }],
  };
}

export function getActionCommentInputMaxLength(limit: number, start: string, separator = ' / '): number {
  return Math.max(0, limit - start.length - separator.length * 2 - ACTION_BLOCK_BOUNDARY_RESERVED_LENGTH);
}

export function toActionCommentPayload(start: string, markdown: string, separator = ' / '): CommentPayload {
  const payload = toCommentPayload(markdown);
  if (!payload.comment) {
    return { comment: start, commentMarkdown: '' };
  }

  const comment = `${start}${separator}${payload.comment}${separator}`;
  if (!payload.commentMarkdown) {
    return { comment, commentMarkdown: '' };
  }

  const document = parseMarkdown(markdown);
  const content = document.content;

  if (!content?.length) {
    return { comment, commentMarkdown: '' };
  }

  const firstBlock = content[0];
  if (firstBlock.type === 'paragraph') {
    firstBlock.content ??= [];
    firstBlock.content.unshift({ type: 'text', text: `${start}${separator}` });
  } else {
    content.unshift(paragraph(`${start}${separator.trimEnd()}`));
  }

  const lastBlock = content.at(-1);
  if (lastBlock?.type === 'paragraph') {
    lastBlock.content ??= [];
    lastBlock.content.push({ type: 'text', text: separator });
  } else {
    content.push(paragraph(separator.trim()));
  }

  const commentMarkdown = serializeMarkdown(document);

  return { comment: toPlainText(commentMarkdown), commentMarkdown };
}
